// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import {
  handleApiCall,
  buildPaginatedParams,
  cachedApiCall,
  apiCache,
} from "../utils/apiHelper";
import {
  getCategoryName,
  getProductTypeName,
  getStatus,
  getEventTypeName,
  getEventSizeName,
  getApplicableForName,
} from "../utils/mappings";

export const productService = {
  /**
   * Get products with pagination and filters
   */
  getProducts: async (filters = {}) => {
    const params = buildPaginatedParams(filters);

    return handleApiCall(
      () => apiClient.get("/admin/catalog/", { params }),
      "Error fetching products",
    );
  },

  /**
   * Get bookable products with default active filter
   */
  getBookableProducts: async (filters = {}) => {
    const params = buildPaginatedParams({
      ...filters,
      vhp_archiving_flag: filters.vhp_archiving_flag || "active",
    });

    return handleApiCall(
      () => apiClient.get("/admin/catalog/", { params }),
      "Error fetching bookable products",
    );
  },

  /**
   * Update a product
   */
  updateProduct: async (productId, productData) => {
    apiCache.clear("products");
    apiCache.clear("filters");

    return handleApiCall(
      () => apiClient.put(`/admin/catalog/${productId}`, productData),
      "Error updating product",
    );
  },

  /**
   * Create a new product
   */
  createProduct: async (productData) => {
    apiCache.clear("products");
    apiCache.clear("filters");
    return handleApiCall(
      () => apiClient.post("/admin/catalog/", productData),
      "Error creating product",
    );
  },

  /**
   * Delete a product
   */
  deleteProduct: async (productId) => {
    apiCache.clear("products");
    apiCache.clear("filters");

    return handleApiCall(
      () => apiClient.delete(`/admin/catalog/${productId}`),
      "Error deleting product",
    );
  },

  /**
   * Export products (API: POST /admin/catalog/export)
   * @param {Object} options
   * @param {string} options.file_type - "csv" | "xlsx" | "json"
   * @param {string} options.scope - "all" | "current_view"
   * @param {number[]} [options.item_ids] - when scope === "current_view"
   * @param {Object} [options.filters] - when scope === "all"
   * @param {string[]} [options.columns] - optional column names to export
   * @param {boolean} [options.include_archived]
   */
  exportProducts: async (options = {}) => {
    const allowedTypes = ["csv", "xlsx", "json"];
    const fileType = allowedTypes.includes(
      String(options.file_type).toLowerCase(),
    )
      ? String(options.file_type).toLowerCase()
      : "csv";
    const scope = options.scope === "current_view" ? "current_view" : "all";

    const body = {
      file_type: fileType,
      scope,
      include_archived: Boolean(options.include_archived),
      filters: options.filters || {},
    };

    if (
      scope === "current_view" &&
      Array.isArray(options.item_ids) &&
      options.item_ids.length > 0
    ) {
      body.item_ids = options.item_ids;
    }
    if (Array.isArray(options.columns) && options.columns.length > 0) {
      body.columns = options.columns;
    }

    return handleApiCall(
      () => apiClient.post("/admin/catalog/export", body),
      "Error exporting products",
    );
  },

  /**
   * Import products from file (API: POST /admin/catalog/import, multipart/form-data)
   * @param {File} file - CSV, XLSX, or JSON file
   * @param {string} fileType - "csv" | "xlsx" | "json"
   * @param {Object} options
   * @param {boolean} [options.overwriteExisting] - update existing by id/external_id
   * @param {boolean} [options.validateOnly] - no write; response reports what would happen
   * @returns {Promise<{success, total_rows, imported_count, updated_count, skipped_count, error_count, errors, warnings}>}
   */
  importProducts: async (file, fileType, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);
    formData.append(
      "overwrite_existing",
      String(Boolean(options.overwriteExisting)),
    );
    formData.append("validate_only", String(Boolean(options.validateOnly)));
    return handleApiCall(
      () =>
        apiClient.post("/admin/catalog/import", formData, {
          headers: {
            "Content-Type": false,
            Accept: "application/json",
          },
        }),
      "Error importing products",
    );
  },

  /**
   * Get all filter options with caching (5 min TTL)
   * Replaces 62+ lines of nested ternary operators with clean mapping functions
   */
  getAllFilters: async () => {
    const cacheKey = "filters:all";
    const cacheTTL = 5 * 60 * 1000;
    return cachedApiCall(
      cacheKey,
      async () => {
        const response = await apiClient.get("/admin/catalog/filters");
        const data = response.data;
        return {
          locations: data.locations.map((location) => ({
            id: location.id,
            name: location.name,
            vhp_code: location.vhp_code,
          })),
          status: data.archiving_flags.map((flag) => ({
            id: flag,
            name: getStatus(flag).text,
          })),
          categories: data.product_categories.map((cat) => ({
            id: cat,
            name: getCategoryName(cat),
          })),
          productTypes: data.product_types.map((type) => ({
            id: type,
            name: getProductTypeName(type),
          })),
          scenarios: data.scenarios.map((scenario) => ({
            id: scenario.id,
            scenario_code: scenario.scenario_code,
            name: scenario.name,
            description: scenario.description,
            event_type: scenario.event_type,
            event_size: scenario.event_size,
          })),
          eventTypes: data.event_types.map((type) => ({
            id: type,
            name: getEventTypeName(type),
          })),
          eventSizes: data.event_sizes.map((size) => ({
            id: size,
            name: getEventSizeName(size),
          })),
          layoutPositions: data.layout_positions,
          paketOptions: data.paket_options,
          applicableForOptions: data.applicable_for_options.map((option) => ({
            id: option,
            name: getApplicableForName(option),
          })),
        };
      },
      cacheTTL,
    );
  },

  /**
   * Clear filters cache manually if needed
   */
  clearFiltersCache: () => {
    apiCache.clear("filters");
  },
};
